import { t, useLanguage } from "@/lib/i18n";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function GuestTreeDialog({ open, onClose }) {
  useLanguage();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [trees, setTrees] = useState([]);
  const [selection, setSelection] = useState({ religion: "", caste: "", gotra: "" });
  const [loading, setLoading] = useState(false);
  const [entering, setEntering] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSelection({ religion: "", caste: "", gotra: "" });
    setTrees([]);
    setLoading(true);
    setError("");
    api.get("/guest-trees").then(({ data }) => {
      if (!cancelled) setTrees(data);
    }).catch((err) => {
      if (!cancelled) setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not load available trees. Please retry.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, retry]);

  const religions = [...new Set(trees.map((tree) => tree.religion))];
  const castes = [...new Set(trees.filter((tree) => tree.religion === selection.religion).map((tree) => tree.caste))];
  const gotras = trees.filter((tree) => tree.religion === selection.religion && tree.caste === selection.caste);
  const selectedTree = gotras.find((tree) => tree.gotra === selection.gotra);
  const enter = async (event) => {
    event.preventDefault();
    if (!selectedTree || entering) return;
    setEntering(true);
    setError("");
    try {
      if (user?.role !== "guest") {
        const { data } = await api.post("/auth/guest");
        login(data);
      }
      navigate(`/app?${new URLSearchParams(selection).toString()}`);
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not open the tree. Please try again.");
    } finally {
      setEntering(false);
    }
  };
  const selectStyle = "h-10 w-full rounded-md border border-input bg-white px-3 text-sm";

  return <Dialog open={open} onOpenChange={(value) => { if (!value && !entering) onClose(); }}>
    <DialogContent data-testid="guest-tree-dialog">
      <DialogHeader>
        <DialogTitle>{t("Explore Vansavali")}</DialogTitle>
        <DialogDescription>{t("Select Religion, Caste and Gotra to open a tree with public members. No account needed.")}</DialogDescription>
      </DialogHeader>
      <form onSubmit={enter} className="space-y-4">
        {loading && <p role="status" className="text-sm text-stone-600">{t("Loading available trees...")}</p>}
        {error && <div role="alert" className="text-sm text-red-700">{t(error)}
          {!trees.length && <Button type="button" variant="outline" className="ml-2" onClick={() => setRetry((value) => value + 1)}>{t("Retry")}</Button>}
        </div>}
        {!loading && !error && !trees.length && <p className="text-sm text-stone-600">{t("No public trees are available yet.")}</p>}
        <div className="space-y-1.5">
          <Label htmlFor="guest-religion">{t("Religion")}</Label>
          <select id="guest-religion" className={selectStyle} required value={selection.religion} disabled={loading || entering}
            onChange={(event) => setSelection({ religion: event.target.value, caste: "", gotra: "" })}>
            <option value="">{t("Select Religion")}</option>
            {religions.map((religion) => <option key={religion} value={religion}>{t(religion)}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="guest-caste">{t("Caste")}</Label>
          <select id="guest-caste" className={selectStyle} required value={selection.caste} disabled={!selection.religion || entering}
            onChange={(event) => setSelection({ ...selection, caste: event.target.value, gotra: "" })}>
            <option value="">{t("Select Caste")}</option>
            {castes.map((caste) => <option key={caste} value={caste}>{caste}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="guest-gotra">{t("Gotra")}</Label>
          <select id="guest-gotra" className={selectStyle} required value={selection.gotra} disabled={!selection.caste || entering}
            onChange={(event) => setSelection({ ...selection, gotra: event.target.value })}>
            <option value="">{t("Select Gotra")}</option>
            {gotras.map((tree) => <option key={tree.gotra} value={tree.gotra}>{tree.gotra} ({tree.member_count}{t(" members)")}</option>)}
          </select>
        </div>
        <Button type="submit" className="w-full gold-btn text-[#2C1E16] font-semibold border-0" disabled={!selectedTree || loading || entering} data-testid="guest-open-tree-button">
          {entering ? t("Opening tree...") : t("Open Tree as Guest")}
        </Button>
      </form>
    </DialogContent>
  </Dialog>;
}
